import React, { useState, useEffect } from 'react';
import { useAuthStore } from '../stores/authStore';
import api from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import AppLayout from '../components/AppLayout';
import { getRoleBadgeStyles } from '../utils/roleColors';
import ManageAccessModal from '../components/ManageAccessModal';

const EXECUTIVE_ROLE_SLUGS = [
    'lider_celula', 
    'discipulador_celula', 
    'pastor_rede', 
    'pastor_senior', 
    'pastor_supervisor', 
    'admin', 
    'tenant_admin'
];

function AccessListPage() {
    const { user } = useAuthStore();
    const [members, setMembers] = useState([]);
    const [roles, setRoles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // States para o Modal
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedMember, setSelectedMember] = useState(null);

    useEffect(() => {
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchData = async () => {
        try {
            setLoading(true);
            setError(null);

            // Carrega membros usando o cliente API padronizado
            const membersResponse = await api.get('/members', {
                params: {
                    tenant_id: user.tenant_id,
                    per_page: 100
                }
            });
            const membersData = membersResponse.data;
            console.log('Members response:', membersData);

            // Carrega perfis (roles) disponíveis usando o cliente API padronizado
            const rolesResponse = await api.get('/roles', {
                params: {
                    tenant_id: user.tenant_id
                }
            });
            const rolesData = rolesResponse.data;

            setMembers(membersData.data || []);
            setRoles(rolesData.data || []);

        } catch (err) {
            console.error('Erro ao carregar dados:', err);
            setError('Não foi possível carregar os dados. Tente novamente.');
        } finally {
            setLoading(false);
        }
    };

    const openManageModal = (member) => {
        setSelectedMember(member);
        setIsModalOpen(true);
    };

    const closeManageModal = () => {
        setIsModalOpen(false);
        setSelectedMember(null);
    };

    const handleModalSuccess = () => {
        closeManageModal();
        fetchData();
    };

    // Filtra para mostrar somente membros com funções executivas (líderes, supervisores, pastores, etc.)

    const executiveMembers = React.useMemo(() => {
        return members.filter(member => 
            member.roles && member.roles.some(r => EXECUTIVE_ROLE_SLUGS.includes(r.slug))
        );
    }, [members]);

    const filteredRoles = React.useMemo(() => {
        const excludedSlugs = ['visitante', 'membro', 'frequentador', 'frequentador_assiduo'];
        return roles.filter(role => !excludedSlugs.includes(role.slug));
    }, [roles]);

    if (loading && members.length === 0) return <LoadingSpinner />;

    return (
        <AppLayout>
            <div className="page-container">
                <div className="page-header">
                    <div>
                        <h1 className="page-title">Acessos</h1>
                        <p className="page-subtitle">Gerencie o acesso ao sistema para os membros e líderes</p>
                    </div>
                </div>

                {error && !isModalOpen && (
                    <div className="alert alert-error mb-4">
                        <span className="material-symbols-outlined">error</span>
                        {error}
                    </div>
                )}

                {/* Lista de Membros e Status de Acesso */}
                <div className="card">
                    <div className="table-responsive">
                        <table className="data-table">
                            <thead>
                                <tr>
                                    <th>Nome</th>
                                    <th>Email</th>
                                    <th>Categoria</th>
                                    <th>Status de Acesso</th>
                                    <th className="text-right">Ações</th>
                                </tr>
                            </thead>
                            <tbody>
                                {executiveMembers.map(member => (
                                    <tr key={member.id}>
                                        <td>
                                            <div className="user-info-cell">
                                                {member.photo_thumbnail_url ? (
                                                    <img src={member.photo_thumbnail_url} alt={member.full_name} className="avatar-img" style={{ width: 32, height: 32, borderRadius: '50%' }} />
                                                ) : (
                                                    <div className="avatar-placeholder" style={{ width: 32, height: 32, borderRadius: '50%', backgroundColor: 'var(--primary-light)', color: 'var(--primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold' }}>
                                                        {member.full_name.charAt(0).toUpperCase()}
                                                    </div>
                                                )}
                                                <div className="ml-3">
                                                    <div className="font-medium">{member.full_name}</div>
                                                </div>
                                            </div>
                                        </td>
                                        <td>{member.email || '-'}</td>
                                        <td>
                                            {member.roles && member.roles.length > 0 ? (
                                                <span className="badge" style={getRoleBadgeStyles(member.roles[0])}>
                                                    {member.roles[0].name}
                                                </span>
                                            ) : (
                                                <span className="badge badge-info">
                                                    {member.category}
                                                </span>
                                            )}
                                        </td>
                                        <td>
                                            {member.wp_user_id ? (
                                                <span className="badge badge-success">
                                                    <span className="material-symbols-outlined" style={{ fontSize: '14px', marginRight: '4px' }}>check_circle</span>
                                                    Acesso Ativo
                                                </span>
                                            ) : (
                                                <span className="badge badge-warning">Sem Acesso</span>
                                            )}
                                        </td>
                                        <td className="text-right">
                                            {!member.wp_user_id ? (
                                                <button
                                                    className="btn btn-primary btn-sm"
                                                    onClick={(e) => { e.stopPropagation(); openManageModal(member); }}
                                                >
                                                    <span className="material-symbols-outlined">vpn_key</span>
                                                    Conceder Acesso
                                                </button>
                                            ) : (
                                                <button
                                                    className="btn btn-outline btn-sm text-primary"
                                                    onClick={(e) => { e.stopPropagation(); openManageModal(member); }}
                                                    title="Gerir Acesso"
                                                >
                                                    <span className="material-symbols-outlined" style={{ fontSize: '18px', marginRight: '4px' }}>key</span>
                                                    Gerir Acesso
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                ))}

                                {executiveMembers.length === 0 && !loading && (
                                    <tr>
                                        <td colSpan="5" className="text-center py-8 text-muted">
                                            Nenhum membro com função executiva encontrado neste tenant.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Modal Gerir Acesso */}
                <ManageAccessModal 
                    isOpen={isModalOpen}
                    onClose={closeManageModal}
                    member={selectedMember}
                    roles={filteredRoles}
                    onSuccess={handleModalSuccess}
                />
            </div>
        </AppLayout>
    );
}

export default AccessListPage;
